import {
  BLOOD_GROUP_CHOICES,
  PATIENT_GENDER_TYPES,
  PATIENT_GENDERS,
} from "@/common/constants";
import { validateName } from "@/common/validation";
import BackButton from "@/components/Common/BackButton";
import Loading from "@/components/Common/Loading";
import Page from "@/components/Common/Page";
import DuplicatePatientDialog from "@/components/Facility/DuplicatePatientDialog";
import { calculateChecksum } from "@/components/Patient/fileChecksum";
import InsuranceCoverageContent from "@/components/Patient/Insurance/InsuranceCoverageContent";
import { insuranceSchema } from "@/components/Patient/Insurance/insuranceSchema";
import { handlePatientRegistrationMutationError } from "@/components/Patient/patientRegistrationErrorHandler";
import { PatientRegistrationFileField } from "@/components/Patient/PatientRegistrationFileField";
import {
  isPatientRegistrationRequestWithinSizeLimit,
  PATIENT_REGISTRATION_PHOTO_MAX_SIZE_MB,
  PATIENT_REGISTRATION_REQUEST_MAX_SIZE_MB,
} from "@/components/Patient/patientRegistrationUploadLimits";
import {
  ALPHABETIC_ONLY_PATTERN,
  ALPHANUMERIC_ONLY_PATTERN,
  hasAnyEmergencyContactValue,
  sanitizeAlphabeticInput,
  sanitizeAlphanumericInput,
  triggerEmergencyContactValidation,
  validateAlphabeticOtherField,
  validateAlphanumericOtherField,
} from "@/components/Patient/patientRegistrationValidators";
import { TagSelectorPopover } from "@/components/Tags/TagAssignmentSheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Autocomplete from "@/components/ui/autocomplete";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import RadioInput from "@/components/ui/RadioInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useShortcutSubContext } from "@/context/ShortcutContext";
import useAppHistory from "@/hooks/useAppHistory";
import useFileManager from "@/hooks/useFileManager";
import useCurrentFacility, {
  useCurrentFacilitySilently,
} from "@/pages/Facility/utils/useCurrentFacility";
import GovtOrganizationSelector from "@/pages/Organization/components/GovtOrganizationSelector";
import { PLUGIN_Component } from "@/PluginEngine";
import {
  BloodGroupChoices,
  BplAplCategory,
  EmergencyContactRelationship,
  PatientCreate,
  PatientIdentifierCreate,
  PatientMaritalStatus,
  PatientRelationshipType,
  PatientUpdate,
} from "@/types/emr/patient/patient";
import patientApi from "@/types/emr/patient/patientApi";
import { PatientFileRecord } from "@/types/emr/patient/patientMasterData";
import patientMasterDataApi from "@/types/emr/patient/patientMasterDataApi";
import { TagConfig, TagResource } from "@/types/emr/tagConfig/tagConfig";
import useTagConfigs from "@/types/emr/tagConfig/useTagConfig";
import { FacilityRead } from "@/types/facility/facility";
import { FileCategory, FileReadMinimal, FileType } from "@/types/files/file";
import fileApi from "@/types/files/fileApi";
import { Organization } from "@/types/organization/organization";
import organizationApi from "@/types/organization/organizationApi";
import { PatientIdentifierConfig } from "@/types/patient/patientIdentifierConfig/patientIdentifierConfig";
import { ShortcutBadge } from "@/Utils/keyboardShortcutComponents";
import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { dateQueryString } from "@/Utils/utils";
import validators from "@/Utils/validators";
import careConfig from "@careConfig";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isFuture } from "date-fns";
import { TFunction } from "i18next";
import { isValidPhoneNumber } from "libphonenumber-js";
import { ArrowLeft, CheckIcon } from "lucide-react";
import { navigate, useNavigationPrompt, useQueryParams } from "raviger";
import { useEffect, useMemo, useState } from "react";
import { Path, useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

const RELATIONSHIP_OPTIONS: {
  labelKey: string;
  value: PatientRelationshipType;
}[] = [
  { value: "father", labelKey: "father" },
  { value: "mother", labelKey: "mother" },
  { value: "spouse", labelKey: "spouse" },
];

const MARITAL_STATUS_OPTIONS: {
  labelKey: string;
  value: PatientMaritalStatus;
}[] = [
  { value: "single", labelKey: "single" },
  { value: "married", labelKey: "married" },
  { value: "divorced", labelKey: "divorced" },
  { value: "widowed", labelKey: "widowed" },
  { value: "separated", labelKey: "separated" },
];

const NOT_SPECIFIED_ID = "__not_specified__";

const EMERGENCY_RELATIONSHIP_OPTIONS: {
  value: EmergencyContactRelationship;
  label: string;
}[] = [
  { value: "spouse", label: "Spouse" },
  { value: "parent", label: "Parent" },
  { value: "child", label: "Child" },
  { value: "sibling", label: "Sibling" },
  { value: "friend", label: "Friend" },
  { value: "guardian", label: "Guardian" },
  { value: "other", label: "Other" },
];

const BPL_APL_OPTIONS: { value: BplAplCategory; labelKey: string }[] = [
  { value: "bpl", labelKey: "bpl" },
  { value: "apl", labelKey: "apl" },
  { value: "not_applicable", labelKey: "not_applicable" },
];

export const PatientRegistration = ({
  patientId,
  facilityId: facilityIdProp,
}: {
  patientId?: string;
  facilityId?: string;
}) => {
  const queryClient = useQueryClient();
  useShortcutSubContext();
  const { t } = useTranslation();
  const { goBack } = useAppHistory();
  const currentFacility = useCurrentFacilitySilently();
  const facilityId = facilityIdProp ?? currentFacility.facilityId;
  const facility = currentFacility.facility;
  const [{ phone_number }] = useQueryParams();

  const [suppressDuplicateWarning, setSuppressDuplicateWarning] =
    useState(!!patientId);

  const [insuranceCardDirty, setInsuranceCardDirty] = useState(false);
  const [insuranceCardOpen, setInsuranceCardOpen] = useState(false);

  const [pendingSubmitValues, setPendingSubmitValues] = useState<
    (PatientRegistrationFormValues & MandatoryPatientBasics) | null
  >(null);

  const [showInsuranceSubmitDialog, setShowInsuranceSubmitDialog] =
    useState(false);

  const quickRegistration =
    careConfig.patientRegistration.minimalPatientRegistration;

  const form = useForm({
    resolver: zodResolver(getFormSchema(t, facility)),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      phone_number: phone_number || "",
      alternate_phone_number: "",
      email: "",
      preferred_language: "",
      emergency_phone_number: "",
      emergency_contact_person_name: "",
      emergency_contact_relationship: undefined,
      emergency_contact_relationship_other: "",
      age_or_dob: "dob",
      permanent_address_same_as_address: true,
      required_identifiers: [],
      optional_identifiers: [],
      autogenerated_identifiers: [],
      government_identifier_config: NOT_SPECIFIED_ID,
      government_identifier_value: "",
      government_identifier_document_file: undefined,
      government_identifier_document_pending: null,
      initial_government_identifier_config: undefined,
      insurance_records: [],
      abha_id_document_file: undefined,
      abha_id_document_pending: null,
      patient_photo_file: undefined,
      patient_photo_pending: null,
      patient_photo_preview: "",
      religion: "",
      religion_other: "",
      caste_category: "",
      caste_category_other: "",
      bpl_apl_category: undefined,
      occupation: "",
      nationality: "",
      tags: [],
      relationship_type: "father",
      relationship_name: "",
      marital_status: undefined,
      _selected_levels: [],
    },
  });

  const validateMandatoryPatientBasicField = (
    field: keyof MandatoryPatientBasics,
    value: string | undefined,
  ) => {
    const trimmedValue = value?.trim() ?? "";

    if (field === "name") {
      if (!trimmedValue) {
        form.setError(field, {
          type: "manual",
          message: t("field_required"),
        });
        return false;
      }

      if (!validateName(trimmedValue)) {
        form.setError(field, {
          type: "manual",
          message: t("min_char_length_error", { min_length: 3 }),
        });
        return false;
      }

      form.clearErrors(field);
      return true;
    }

    if (!trimmedValue) {
      form.setError(field, {
        type: "manual",
        message: t("field_required"),
      });
      return false;
    }

    form.clearErrors(field);
    return true;
  };

  const validateMandatoryPatientBasics = (
    values: PatientRegistrationFormValues,
  ): values is PatientRegistrationFormValues & MandatoryPatientBasics => {
    const isNameValid = validateMandatoryPatientBasicField("name", values.name);
    const isGenderValid = validateMandatoryPatientBasicField(
      "gender",
      values.gender,
    );
    const isRelationshipTypeValid = validateMandatoryPatientBasicField(
      "relationship_type",
      values.relationship_type,
    );
    const isRelationshipNameValid = validateMandatoryPatientBasicField(
      "relationship_name",
      values.relationship_name,
    );

    return (
      isNameValid &&
      isGenderValid &&
      isRelationshipTypeValid &&
      isRelationshipNameValid
    );
  };

  const patientQuery = useQuery({
    queryKey: ["patient", patientId, facilityId ?? null],
    queryFn: query(patientApi.get, {
      pathParams: { id: patientId || "" },
      queryParams: facilityId ? { facility: facilityId } : undefined,
    }),
    enabled: !!patientId,
  });

  const initialInsuranceRecords = useMemo(
    () => patientQuery.data?.insurance_records ?? [],
    [patientQuery.data?.insurance_records],
  );

  const defaultGeoOrgId = careConfig.patientRegistration.defaultGeoOrganization;

  const { data: defaultGeoOrg } = useQuery({
    queryKey: ["organization", defaultGeoOrgId],
    queryFn: query(organizationApi.get, {
      pathParams: { id: defaultGeoOrgId },
    }),
    enabled: !!defaultGeoOrgId,
  });

  useEffect(() => {
    if (!facility) {
      return;
    }

    if (!patientId && defaultGeoOrgId && !defaultGeoOrg) {
      return;
    }

    const data = patientQuery.data;

    // Default values for new patient only
    if (!data && !patientId) {
      const getValue = ({ id }: { id: string }) => ({
        config: id,
        value: "",
      });
      form.setValue(
        "required_identifiers",
        getRequiredIdentifierConfigs(facility).map(getValue),
      );
      form.setValue(
        "optional_identifiers",
        getNonGovernmentOptionalIdentifierConfigs(facility).map(getValue),
      );
      form.setValue(
        "autogenerated_identifiers",
        getAutogeneratedIdentifierConfigs(facility).map(getValue),
      );
      // initial identifier copies for new patient
      form.setValue(
        "initial_required_identifiers",
        getRequiredIdentifierConfigs(facility).map(getValue),
      );
      form.setValue(
        "initial_optional_identifiers",
        getNonGovernmentOptionalIdentifierConfigs(facility).map(getValue),
      );
      form.setValue(
        "initial_autogenerated_identifiers",
        getAutogeneratedIdentifierConfigs(facility).map(getValue),
      );
      form.setValue("government_identifier_config", NOT_SPECIFIED_ID);
      form.setValue("government_identifier_value", "");
      form.setValue("government_identifier_document_file", undefined);
      form.setValue("government_identifier_document_pending", null);
      form.setValue("initial_government_identifier_config", undefined);
      // mark initial optional scalar fields as empty for new patient
      form.setValue("initial_alternate_phone_number", "");
      form.setValue("initial_email", "");
      form.setValue("initial_emergency_phone_number", "");
      form.setValue("initial_emergency_contact_person_name", "");
      form.setValue("initial_emergency_contact_relationship", undefined);
      form.setValue("initial_emergency_contact_relationship_other", "");
      form.setValue("initial_religion", "");
      form.setValue("initial_caste_category", "");
      form.setValue("initial_occupation", "");
      form.setValue("initial_nationality", "");
      form.setValue("initial_nationality_label", "");
      form.setValue("initial_government_identifier_document_file_id", null);
      form.setValue("initial_abha_id_document_file_id", null);
      form.setValue("initial_patient_photo_file_id", null);
      form.setValue("geo_organization", defaultGeoOrgId);
      form.setValue("_selected_levels", [defaultGeoOrg!]);

      return;
    }

    // Existing patient still loading
    if (!data) {
      return;
    }

    const getIdentifier = ({ id }: PatientIdentifierConfig) => {
      const identifier = data.instance_identifiers.find(
        (i) => i.config.id === id,
      );
      return {
        config: id,
        value: identifier?.value,
      };
    };

    // Reset the form with the patient data
    const initialGovernmentIdentifier = getPrimaryIdentityDropdownConfigs(
      facility,
    )
      .map(getIdentifier)
      .find((identifier) => identifier.value);

    const hasSeparatePermanentAddress =
      !!data.permanent_address?.trim() &&
      data.permanent_address !== data.address;

    form.reset({
      name: data.name || "",
      phone_number: data.phone_number || "",
      alternate_phone_number: data.alternate_phone_number || "",
      email: data.email || "",
      preferred_language: data.preferred_language || "",
      emergency_phone_number: data.emergency_phone_number || "",
      emergency_contact_person_name: data.emergency_contact_person_name || "",
      emergency_contact_relationship:
        data.emergency_contact_relationship || undefined,
      emergency_contact_relationship_other:
        data.emergency_contact_relationship_other || "",
      gender: PATIENT_GENDERS.includes(
        data.gender as (typeof PATIENT_GENDERS)[number],
      )
        ? (data.gender as (typeof PATIENT_GENDERS)[number])
        : undefined,
      age_or_dob: data.date_of_birth ? "dob" : "age",
      date_of_birth: data.date_of_birth || undefined,
      age:
        !data.date_of_birth && data.year_of_birth
          ? new Date().getFullYear() - data.year_of_birth
          : undefined,
      blood_group: data.blood_group || undefined,
      tags: [],
      relationship_type:
        data.relationship_type &&
        RELATIONSHIP_OPTIONS.some(
          ({ value }) => value === data.relationship_type,
        )
          ? data.relationship_type
          : "father",
      relationship_name: data.relationship_name || "",
      marital_status:
        data.marital_status &&
        MARITAL_STATUS_OPTIONS.some(
          ({ value }) => value === data.marital_status,
        )
          ? data.marital_status
          : undefined,

      address: data.address || "",
      permanent_address: hasSeparatePermanentAddress
        ? data.permanent_address || ""
        : "",
      permanent_address_same_as_address: !hasSeparatePermanentAddress,
      geo_organization: data.geo_organization?.id || "",
      pincode: data.pincode || undefined,

      // initial identifier copies so we can detect clears
      initial_required_identifiers:
        getRequiredIdentifierConfigs(facility).map(getIdentifier),
      initial_optional_identifiers:
        getNonGovernmentOptionalIdentifierConfigs(facility).map(getIdentifier),
      initial_autogenerated_identifiers:
        getAutogeneratedIdentifierConfigs(facility).map(getIdentifier),

      // initial scalar values
      initial_alternate_phone_number: data.alternate_phone_number || undefined,
      initial_email: data.email || undefined,
      initial_emergency_phone_number: data.emergency_phone_number || undefined,
      initial_emergency_contact_person_name:
        data.emergency_contact_person_name || undefined,
      initial_emergency_contact_relationship:
        data.emergency_contact_relationship || undefined,
      initial_emergency_contact_relationship_other:
        data.emergency_contact_relationship_other || undefined,

      initial_religion: data.religion?.id || "",
      initial_caste_category: data.caste_category?.id || "",
      initial_occupation: data.occupation || "",
      initial_nationality: data.nationality?.id || "",
      initial_nationality_label: data.nationality?.name || "",

      // initial file ids
      initial_government_identifier_document_file_id:
        data.id_proof_document_file?.id || null,
      initial_abha_id_document_file_id: data.abha_id_document_file?.id || null,
      initial_patient_photo_file_id: data.patient_photo_file?.id || null,

      required_identifiers:
        getRequiredIdentifierConfigs(facility).map(getIdentifier),
      optional_identifiers:
        getNonGovernmentOptionalIdentifierConfigs(facility).map(getIdentifier),
      autogenerated_identifiers:
        getAutogeneratedIdentifierConfigs(facility).map(getIdentifier),
      government_identifier_config:
        initialGovernmentIdentifier?.config || NOT_SPECIFIED_ID,
      government_identifier_value: initialGovernmentIdentifier?.value || "",
      government_identifier_document_file:
        data.id_proof_document_file || undefined,
      government_identifier_document_pending: null,
      initial_government_identifier_config: initialGovernmentIdentifier?.config,
      abha_id_document_file: data.abha_id_document_file || undefined,
      abha_id_document_pending: null,
      patient_photo_file: data.patient_photo_file || undefined,
      patient_photo_pending: null,
      patient_photo_preview: "",
      religion: data.religion?.id || "",
      religion_other: data.religion_other || "",
      caste_category: data.caste_category?.id || "",
      caste_category_other: data.caste_category_other || "",
      bpl_apl_category: data.bpl_apl_category || undefined,
      occupation: data.occupation || "",
      nationality: data.nationality?.id || "",

      _selected_levels: [data.geo_organization],
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientQuery.data, facility, defaultGeoOrg]);

  const phoneNumber = form.watch("phone_number");
  const patientPhoneSearch = useQuery({
    queryKey: ["patients", "phone-number", phoneNumber],
    queryFn: query.debounced(patientApi.search, {
      body: { phone_number: phoneNumber },
    }),
    enabled: isValidPhoneNumber(phoneNumber),
  });

  const duplicatePatients = patientPhoneSearch.data?.results.filter(
    (p) => p.id !== patientId,
  );

  const { mutateAsync: createPatient, isPending: isCreatingPatient } =
    useMutation({
      mutationKey: ["create_patient"],
      mutationFn: mutate(patientApi.create),
      onError: handlePatientRegistrationMutationError,
    });

  const { mutateAsync: updatePatient, isPending: isUpdatingPatient } =
    useMutation({
      mutationKey: ["update_patient"],
      mutationFn: mutate(patientApi.update, {
        pathParams: { id: patientId || "" },
        queryParams: facilityId ? { facility: facilityId } : undefined,
      }),
      onError: handlePatientRegistrationMutationError,
    });

  const isPending = isCreatingPatient || isUpdatingPatient;

  const showDuplicate =
    !patientPhoneSearch.isLoading &&
    !!duplicatePatients?.length &&
    !!isValidPhoneNumber(phoneNumber) &&
    !suppressDuplicateWarning;

  useNavigationPrompt(
    form.formState.isDirty &&
      !form.formState.isSubmitting &&
      !isPending &&
      !showDuplicate,
    t("unsaved_changes"),
  );

  const fileManager = useFileManager({ type: FileType.PATIENT });

  if (patientId && patientQuery.isLoading) {
    return <Loading />;
  }

  function buildPatientPayload(
    values: PatientRegistrationFormValues & MandatoryPatientBasics,
  ): PatientCreate | PatientUpdate {
    const {
      name,
      gender,
      relationship_type,
      relationship_name,
      age_or_dob,
      date_of_birth,
      age,
      permanent_address_same_as_address,
      address,
      permanent_address,
      government_identifier_config,
      government_identifier_value,
      government_identifier_document_file,
      government_identifier_document_pending:
        _governmentIdentifierDocumentPending,
      abha_id_document_file,
      abha_id_document_pending: _abhaIdDocumentPending,
      patient_photo_file,
      patient_photo_pending: _patientPhotoPending,
      patient_photo_preview: _patientPhotoPreview,
      religion,
      religion_other,
      caste_category,
      caste_category_other,
      nationality,
      initial_government_identifier_config,
      insurance_records,
      tags: _tags,
      _selected_levels: _selectedLevels,
      required_identifiers,
      optional_identifiers,
      autogenerated_identifiers,
      emergency_contact_relationship,
      emergency_contact_relationship_other,
      emergency_contact_person_name,
      emergency_phone_number,
      alternate_phone_number,
      email,
      preferred_language,
      blood_group,
      bpl_apl_category,
      occupation,
    } = values;
    const toNullableString = (current?: string | null) => {
      const trimmed = current?.trim() ?? "";
      return trimmed || null;
    };

    const resolvedAlternatePhone = toNullableString(alternate_phone_number);
    const resolvedEmail = toNullableString(email);
    const resolvedEmergencyPhone = toNullableString(emergency_phone_number);
    const resolvedEmergencyPerson = toNullableString(
      emergency_contact_person_name,
    );
    const resolvedEmergencyRelationship: EmergencyContactRelationship | null =
      emergency_contact_relationship ?? null;
    const resolvedEmergencyRelationshipOther =
      emergency_contact_relationship === "other"
        ? toNullableString(emergency_contact_relationship_other)
        : null;

    const resolvedReligion = toNullableString(religion as string | null);
    const resolvedCaste = toNullableString(caste_category as string | null);
    const resolvedOccupation = toNullableString(occupation);
    const resolvedNationality = toNullableString(nationality as string | null);

    let resolvedPatientPhotoFile: string | null | undefined;
    if (patient_photo_file?.id) {
      resolvedPatientPhotoFile = patient_photo_file.id;
    } else {
      resolvedPatientPhotoFile = null;
    }

    let resolvedGovIdFile: string | null | undefined;
    if (government_identifier_document_file?.id) {
      resolvedGovIdFile = government_identifier_document_file.id;
    } else {
      resolvedGovIdFile = null;
    }

    let resolvedAbhaFile: string | null | undefined;
    if (abha_id_document_file?.id) {
      resolvedAbhaFile = abha_id_document_file.id;
    } else {
      resolvedAbhaFile = null;
    }

    const payload: any = {
      phone_number: values.phone_number,
      name: name.trim(),
      gender,
      blood_group,
      alternate_phone_number: resolvedAlternatePhone,
      email: resolvedEmail,
      preferred_language: preferred_language?.trim() || null,
      emergency_phone_number: resolvedEmergencyPhone,
      emergency_contact_person_name: resolvedEmergencyPerson,
      emergency_contact_relationship: resolvedEmergencyRelationship,
      emergency_contact_relationship_other: resolvedEmergencyRelationshipOther,
      age: age_or_dob === "dob" ? undefined : age || undefined,
      date_of_birth:
        age_or_dob === "dob" ? date_of_birth || undefined : undefined,
      // Backend enforces non-null for address/permanent_address.
      address: (address ?? "").trim(),
      permanent_address: permanent_address_same_as_address
        ? (address ?? "").trim()
        : (permanent_address ?? "").trim(),
      geo_organization: values.geo_organization,
      pincode: values.pincode,
      relationship_type,
      relationship_name: relationship_name.trim(),
      marital_status: values.marital_status ?? null,
      religion: resolvedReligion as any,
      religion_other: religion_other?.trim() || undefined,
      caste_category: resolvedCaste as any,
      caste_category_other: caste_category_other?.trim() || undefined,
      bpl_apl_category: bpl_apl_category ?? null,
      occupation: resolvedOccupation as any,
      nationality: resolvedNationality as any,
      patient_photo_file: resolvedPatientPhotoFile as any,
      id_proof_document_file: resolvedGovIdFile as any,
      insurance_records: insurance_records?.map((record: any) => ({
        ...(record.id ? { id: record.id } : {}),

        coverage_type: record.coverage_type || null,

        government_scheme: record.government_scheme || null,

        government_scheme_other: record.government_scheme_other || "",

        insurance_provider_name: record.insurance_provider_name || "",

        tpa_name: record.tpa_name || "",

        policy_or_card_number: record.policy_or_card_number || "",

        policy_validity_date: record.policy_validity_date || null,

        pre_authorization_required: record.pre_authorization_required ?? null,

        insurance_card_letter_file:
          typeof record.insurance_card_letter_file === "string"
            ? record.insurance_card_letter_file
            : (record.insurance_card_letter_file?.id ?? null),
      })),
      abha_id_document_file: resolvedAbhaFile as any,
      identifiers: getPatientIdentifiersPayload(
        {
          ...values,
          government_identifier_config,
          government_identifier_value,
          initial_government_identifier_config,
          required_identifiers,
          optional_identifiers,
          autogenerated_identifiers,
        },
        facility!,
      ),
    };

    // Sanitize empty-string optional fields to null so backend validation accepts clears
    const optionalStringFields = [
      "alternate_phone_number",
      "email",
      "emergency_phone_number",
      "emergency_contact_person_name",
      "religion",
      "caste_category",
      "occupation",
      "nationality",
      "patient_photo_file",
      "id_proof_document_file",
      "abha_id_document_file",
    ];

    for (const key of optionalStringFields) {
      if (payload[key] === "") {
        payload[key] = null;
      }
    }

    return payload as PatientCreate | PatientUpdate;
  }

  const collectRegistrationFileEntries = (
    values: PatientRegistrationFormValues,
  ) => {
    const entries: {
      fieldName: string;
      pendingFile?: File | null;
      existingFile?: PatientFileRecord | null;
      errorPath: Path<PatientRegistrationFormValues>;
      label: string;
    }[] = [
      {
        fieldName: "patient_photo_file",
        pendingFile: values.patient_photo_pending,
        existingFile: values.patient_photo_file,
        errorPath: "patient_photo_file",
        label: t("patient_photo"),
      },
      {
        fieldName: "id_proof_document_file",
        pendingFile: values.government_identifier_document_pending,
        existingFile: values.government_identifier_document_file,
        errorPath: "government_identifier_document_file",
        label: t("id_proof_document"),
      },
      {
        fieldName: "abha_id_document_file",
        pendingFile: values.abha_id_document_pending,
        existingFile: values.abha_id_document_file,
        errorPath: "abha_id_document_file",
        label: t("abha_id_document"),
      },
    ];

    values.insurance_records?.forEach((record, index) => {
      entries.push({
        fieldName: `insurance_records.${index}.insurance_card_letter_file`,
        pendingFile: record.insurance_card_letter_pending,
        existingFile: record.insurance_card_letter_file,
        errorPath:
          `insurance_records.${index}.insurance_card_letter_file` as Path<PatientRegistrationFormValues>,
        label: t("insurance_document"),
      });
    });

    return entries;
  };

  const validatePendingRegistrationRequestSize = (
    values: PatientRegistrationFormValues,
  ) => {
    if (isPatientRegistrationRequestWithinSizeLimit(values)) {
      return true;
    }

    toast.error(
      t("patient_registration_files_size_exceeds_limit", {
        size: PATIENT_REGISTRATION_REQUEST_MAX_SIZE_MB,
      }),
    );
    return false;
  };

  async function validatePendingRegistrationFileDuplicates(
    values: PatientRegistrationFormValues,
  ) {
    const entries = collectRegistrationFileEntries(values);
    const existingChecksums = entries
      .filter((entry) => entry.existingFile?.checksum)
      .map((entry) => ({
        fieldName: entry.fieldName,
        checksum: entry.existingFile!.checksum!,
        label: entry.label,
      }));
    const pendingChecksums: {
      fieldName: string;
      checksum: string;
      errorPath: Path<PatientRegistrationFormValues>;
      label: string;
    }[] = [];

    let hasDuplicates = false;

    for (const entry of entries) {
      if (!entry.pendingFile) {
        continue;
      }

      const checksum = await calculateChecksum(entry.pendingFile);
      const duplicatePending = pendingChecksums.find(
        (pending) => pending.checksum === checksum,
      );
      const duplicateExisting = existingChecksums.find(
        (existing) =>
          existing.fieldName !== entry.fieldName &&
          existing.checksum === checksum,
      );

      if (duplicatePending || duplicateExisting) {
        const duplicateLabel =
          duplicatePending?.label ?? duplicateExisting?.label ?? t("document");
        const message = t("duplicate_patient_registration_file", {
          file: entry.label,
          duplicate: duplicateLabel,
        });

        toast.error(message);
        hasDuplicates = true;
        continue;
      }

      pendingChecksums.push({
        fieldName: entry.fieldName,
        checksum,
        errorPath: entry.errorPath,
        label: entry.label,
      });
    }

    return !hasDuplicates;
  }

  function buildPatientRegistrationFormData(
    payload: PatientCreate | PatientUpdate,
    values: PatientRegistrationFormValues,
  ) {
    const formData = new FormData();

    formData.append("patient", JSON.stringify(payload));

    for (const entry of collectRegistrationFileEntries(values)) {
      if (entry.pendingFile) {
        formData.append(entry.fieldName, entry.pendingFile);
      }
    }

    return formData;
  }

  async function onSubmit(values: PatientRegistrationFormValues) {
    if (!facility) {
      return;
    }

    if (!validateMandatoryPatientBasics(values)) {
      return;
    }

    if (!validatePendingRegistrationRequestSize(values)) {
      return;
    }

    if (insuranceCardOpen && insuranceCardDirty) {
      setPendingSubmitValues(
        values as PatientRegistrationFormValues & MandatoryPatientBasics,
      );

      setShowInsuranceSubmitDialog(true);
      return;
    }

    const hasUniquePendingFiles =
      await validatePendingRegistrationFileDuplicates(values);

    if (!hasUniquePendingFiles) {
      return;
    }

    const basePayload = buildPatientPayload(values);

    try {
      if (!patientId) {
        const payload = {
          ...(basePayload as PatientCreate),
          facility: facilityId,
          tags: values.tags,
        };
        const resp = await createPatient(
          buildPatientRegistrationFormData(payload, values),
        );

        toast.success(t("patient_registration_success"));
        navigate(`/facility/${facilityId}/patients/verify`, {
          query: {
            phone_number: resp.phone_number,
            year_of_birth: resp.year_of_birth,
            partial_id: resp?.id?.slice(0, 5),
          },
        });
      } else {
        await updatePatient(
          buildPatientRegistrationFormData(basePayload, values),
        );

        await queryClient.invalidateQueries({
          queryKey: ["patient", patientId, facilityId ?? null],
        });

        toast.success(t("patient_update_success"));

        form.reset(form.getValues());

        goBack();
      }
    } catch {
      // Mutation errors are surfaced by the request layer.
    }
  }

  const pageTitle = !patientId
    ? t("register_new_patient")
    : t("update_patient_details");

  return (
    <Page title={pageTitle} hideTitleOnPage>
      <div className="flex flex-col gap-4 max-w-2xl mx-auto">
        <div>
          <BackButton data-shortcut-id="go-back">
            <ArrowLeft />
            {t("back")}
          </BackButton>
        </div>
        <div className="pb-4 mb-2 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex flex-col">
              <h4 className="font-semibold">{pageTitle}</h4>
              {!patientId && (
                <span className="text-sm text-gray-700">
                  {t("patient_create_description")}
                </span>
              )}
            </div>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Accordion
              type="multiple"
              className="flex flex-col gap-6"
              defaultValue={
                patientId
                  ? [
                      "patient-basics",
                      "additional-details",
                      "insurance-coverage",
                    ]
                  : ["patient-basics"]
              }
            >
              <PLUGIN_Component
                __name="PatientRegistrationForm"
                form={form}
                facilityId={facilityId}
                patientId={patientId}
              />
              <AccordionItem
                value="patient-basics"
                className="bg-white flex flex-col gap-4 p-6 shadow rounded-md"
              >
                <AccordionTrigger className="flex justify-between">
                  <div className="flex gap-3 items-center w-full">
                    <h5 className="text-lg font-semibold">
                      1: {t("patient_basics")}
                    </h5>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-1 flex flex-col gap-6">
                  <PatientBasicsContent
                    form={form}
                    isCreate={!patientId}
                    validateMandatoryPatientBasicField={
                      validateMandatoryPatientBasicField
                    }
                  />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem
                value="additional-details"
                className="bg-white flex flex-col gap-4 p-6 shadow rounded-md"
              >
                <AccordionTrigger className="flex justify-between">
                  <div className="flex gap-3 items-center w-full">
                    <h5 className="text-lg font-semibold">
                      2: {t("additional_details")}{" "}
                      {quickRegistration && `(${t("optional")})`}
                    </h5>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-1 flex flex-col gap-6">
                  <AdditionalDetailsContent
                    form={form}
                    isCreate={!patientId}
                    patientId={patientId}
                  />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem
                value="insurance-coverage"
                className="bg-white flex flex-col gap-4 p-6 shadow rounded-md"
              >
                <AccordionTrigger className="flex justify-between">
                  <div className="flex gap-3 items-center w-full">
                    <h5 className="text-lg font-semibold">
                      3: {t("insurance_coverage")}
                      {quickRegistration && ` (${t("optional")})`}
                    </h5>
                  </div>
                </AccordionTrigger>

                <InsuranceCoverageContent
                  form={form}
                  patientId={patientId}
                  initialInsuranceRecords={initialInsuranceRecords}
                  fileManager={fileManager}
                  onInsuranceDirtyChange={setInsuranceCardDirty}
                  onCardOpenChange={setInsuranceCardOpen}
                />
              </AccordionItem>
            </Accordion>
            <div className="w-full border-t border-gray-200 py-6 mt-6">
              <div className="max-w-2xl mx-auto flex justify-end">
                <Button
                  variant="primary_gradient"
                  disabled={!form.formState.isDirty || isPending}
                >
                  <CheckIcon />
                  {patientId ? t("update") : t("register_patient")}
                  <ShortcutBadge actionId="submit-action" />
                </Button>
              </div>
            </div>
          </form>
          {fileManager.Dialogues}

          <AlertDialog
            open={showInsuranceSubmitDialog}
            onOpenChange={setShowInsuranceSubmitDialog}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("discard_unsaved_insurance")}
                </AlertDialogTitle>

                <AlertDialogDescription>
                  {t("discard_unsaved_insurance_continue")}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>

                <AlertDialogAction
                  onClick={async () => {
                    if (!pendingSubmitValues) return;

                    setShowInsuranceSubmitDialog(false);

                    if (
                      !validatePendingRegistrationRequestSize(
                        pendingSubmitValues,
                      )
                    ) {
                      setPendingSubmitValues(null);
                      return;
                    }

                    const hasUniquePendingFiles =
                      await validatePendingRegistrationFileDuplicates(
                        pendingSubmitValues,
                      );

                    if (!hasUniquePendingFiles) {
                      setPendingSubmitValues(null);
                      return;
                    }

                    const basePayload =
                      buildPatientPayload(pendingSubmitValues);

                    try {
                      if (!patientId) {
                        const payload = {
                          ...(basePayload as PatientCreate),
                          facility: facilityId,
                          tags: pendingSubmitValues.tags,
                        };
                        const resp = await createPatient(
                          buildPatientRegistrationFormData(
                            payload,
                            pendingSubmitValues,
                          ),
                        );

                        setInsuranceCardDirty(false);
                        setInsuranceCardOpen(false);

                        toast.success(t("patient_registration_success"));

                        navigate(`/facility/${facilityId}/patients/verify`, {
                          query: {
                            phone_number: resp.phone_number,
                            year_of_birth: resp.year_of_birth,
                            partial_id: resp.id.slice(0, 5),
                          },
                        });
                      } else {
                        await updatePatient(
                          buildPatientRegistrationFormData(
                            basePayload,
                            pendingSubmitValues,
                          ),
                        );

                        setInsuranceCardDirty(false);
                        setInsuranceCardOpen(false);

                        toast.success(t("patient_update_success"));

                        form.reset(form.getValues());

                        goBack();
                      }
                    } finally {
                      setPendingSubmitValues(null);
                    }
                  }}
                >
                  {t("discard_and_continue")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Form>
      </div>

      {showDuplicate && (
        <DuplicatePatientDialog
          open={showDuplicate}
          patientList={duplicatePatients}
          onTransfer={() =>
            navigate(`/facility/${facilityId}/patients`, {
              query: { phone_number: form.getValues("phone_number") },
            })
          }
          onOpenChange={(open) => {
            if (!open) {
              setSuppressDuplicateWarning(true);
            }
          }}
        />
      )}
    </Page>
  );
};

const PatientBasicsContent = ({
  form,
  isCreate,
  validateMandatoryPatientBasicField,
}: {
  form: UseFormReturn<z.infer<ReturnType<typeof getFormSchema>>>;
  isCreate: boolean;
  validateMandatoryPatientBasicField: (
    field: keyof MandatoryPatientBasics,
    value: string | undefined,
  ) => boolean;
}) => {
  const { t } = useTranslation();
  const {
    patientRegistration: { minGeoOrganizationLevelsRequired },
  } = careConfig;

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel aria-required>{t("name")}</FormLabel>
            <FormControl>
              <Input
                autoFocus
                tabIndex={0}
                placeholder={t("type_name")}
                {...field}
                value={field.value ?? ""}
                onBlur={() => {
                  field.onBlur();
                  validateMandatoryPatientBasicField("name", field.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Tab" || event.key === "Enter") {
                    validateMandatoryPatientBasicField("name", field.value);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="relationship_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel aria-required>{t("father_mother_spouse")}</FormLabel>
            <FormControl>
              <RadioInput
                {...field}
                onValueChange={field.onChange}
                value={field.value ?? ""}
                required={true}
                onBlur={() => {
                  field.onBlur();
                  validateMandatoryPatientBasicField(
                    "relationship_type",
                    field.value,
                  );
                }}
                onKeyDown={(event) => {
                  if (event.key === "Tab" || event.key === "Enter") {
                    validateMandatoryPatientBasicField(
                      "relationship_type",
                      field.value,
                    );
                  }
                }}
                options={RELATIONSHIP_OPTIONS.map((option) => ({
                  value: option.value,
                  label: t(option.labelKey),
                }))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="relationship_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel aria-required>
              {t("father_mother_spouse_name")}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={t("enter_father_mother_spouse_name")}
                value={field.value ?? ""}
                onBlur={() => {
                  field.onBlur();
                  validateMandatoryPatientBasicField(
                    "relationship_name",
                    field.value,
                  );
                }}
                onKeyDown={(event) => {
                  if (event.key === "Tab" || event.key === "Enter") {
                    validateMandatoryPatientBasicField(
                      "relationship_name",
                      field.value,
                    );
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="age_or_dob"
        render={({ field }) => (
          <FormItem>
            <FormLabel aria-required>{t("date_of_birth_or_age")}</FormLabel>
            <div className="flex gap-1 items-start">
              <Tabs value={field.value} onValueChange={field.onChange}>
                <TabsList className="mt-1 md:mt-px">
                  <TabsTrigger value="dob">{t("date")}</TabsTrigger>
                  <TabsTrigger value="age">{t("age")}</TabsTrigger>
                </TabsList>
              </Tabs>
              {field.value === "dob" && (
                <FormField
                  control={form.control}
                  name="date_of_birth"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <DatePicker
                          date={field.value ? new Date(field.value) : undefined}
                          onChange={(date) => {
                            if (date) {
                              field.onChange(dateQueryString(date));
                              form.clearErrors("date_of_birth");
                              form.trigger("date_of_birth");
                            } else {
                              field.onChange(undefined);
                            }
                          }}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            return date > today;
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {field.value === "age" && (
                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <FormItem className="w-full md:col-span-2 relative">
                      <div className="relative">
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder={t("age")}
                            min={1}
                            max={130}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value ? Number(e.target.value) : null,
                              );
                            }}
                          />
                        </FormControl>
                        {field.value !== null && field.value !== undefined && (
                          <span className="text-xs text-gray-500 absolute right-9 top-3.25 md:top-2.5">
                            {`${t("year_of_birth")}: `}
                            {new Date().getFullYear() - Number(field.value)}
                          </span>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="gender"
        render={({ field }) => (
          <FormItem>
            <FormLabel aria-required>{t("gender")}</FormLabel>
            <FormControl>
              <RadioInput
                {...field}
                onValueChange={field.onChange}
                value={field.value ?? ""}
                required={true}
                onBlur={() => {
                  field.onBlur();
                  validateMandatoryPatientBasicField("gender", field.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Tab" || event.key === "Enter") {
                    validateMandatoryPatientBasicField("gender", field.value);
                  }
                }}
                options={PATIENT_GENDER_TYPES.map((g) => ({
                  value: g.id,
                  label: t(`GENDER__${g.id}`),
                }))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <FormField
          control={form.control}
          name="marital_status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("marital_status")}</FormLabel>
              <FormControl>
                <Select
                  value={field.value ?? ""}
                  onValueChange={(value) =>
                    field.onChange(value === "__unset__" ? undefined : value)
                  }
                >
                  <FormControl>
                    <SelectTrigger ref={field.ref}>
                      <SelectValue placeholder={t("select_marital_status")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="__unset__">
                      {t("not_specified")}
                    </SelectItem>
                    {MARITAL_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel aria-required>{t("mobile_number")}</FormLabel>
              <FormControl>
                <PhoneInput
                  {...field}
                  placeholder={t("enter_mobile_number")}
                  onChange={(value) => {
                    const phoneValue = value ?? "";
                    field.onChange(phoneValue);
                    if (!phoneValue.trim()) {
                      form.clearErrors("phone_number");
                    }
                  }}
                  onBlur={() => {
                    field.onBlur();
                    const value = (form.getValues("phone_number") ?? "").trim();
                    if (!value) {
                      form.clearErrors("phone_number");
                      return;
                    }
                    void form.trigger("phone_number");
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <FormField
          control={form.control}
          name="alternate_phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("alternate_mobile_number")}</FormLabel>
              <FormControl>
                <PhoneInput
                  {...field}
                  placeholder={t("enter_mobile_number")}
                  onChange={(value) => field.onChange(value ?? "")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="relative">
              <FormLabel>{t("email_id")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder={t("example_email_address")}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage className="absolute left-0 top-full mt-2" />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <FormField
          control={form.control}
          name="blood_group"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("blood_group")}</FormLabel>
              <Select value={field.value ?? ""} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger ref={field.ref}>
                    <SelectValue placeholder={t("select_blood_group")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {BLOOD_GROUP_CHOICES.map((bg) => (
                    <SelectItem key={bg.id} value={bg.id}>
                      {bg.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="preferred_language"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("preferred_language")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ""}
                  placeholder={t("enter_preferred_language")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="geo_organization"
          render={({ field }) => (
            <FormItem className="col-span-2 flex flex-col gap-1.5">
              <FormControl>
                <GovtOrganizationSelector
                  {...field}
                  requiredDepth={
                    isCreate ? (minGeoOrganizationLevelsRequired ?? 1) : 1
                  }
                  selected={form.watch("_selected_levels")}
                  value={form.watch("geo_organization")}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage className="text-red-500 text-sm" />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};

const AdditionalDetailsContent = ({
  form,
  isCreate,
  patientId,
}: {
  form: UseFormReturn<z.infer<ReturnType<typeof getFormSchema>>>;
  isCreate: boolean;
  patientId?: string;
}) => {
  const { t } = useTranslation();
  const { facility, facilityId } = useCurrentFacility();
  const [religionSearch, setReligionSearch] = useState("");
  const [casteSearch, setCasteSearch] = useState("");
  const [nationalitySearch, setNationalitySearch] = useState("");

  const tagIds = form.watch("tags") || [];
  const selectedTags = useTagConfigs({ ids: tagIds, facilityId })
    .map(({ data }) => data)
    .filter(Boolean) as TagConfig[];
  const primaryIdentityConfigs = facility
    ? getPrimaryIdentityDropdownConfigs(facility)
    : [];
  const selectedGovernmentId =
    form.watch("government_identifier_config") === NOT_SPECIFIED_ID
      ? undefined
      : form.watch("government_identifier_config");
  const selectedPrimaryIdentityConfig = primaryIdentityConfigs.find(
    ({ id }) => id === selectedGovernmentId,
  );

  const religionsQuery = useQuery({
    queryKey: ["religions", religionSearch],
    queryFn: query.paginated(patientMasterDataApi.listReligions, {
      queryParams: religionSearch ? { name: religionSearch } : {},
    }),
  });
  const casteQuery = useQuery({
    queryKey: ["caste_categories", casteSearch],
    queryFn: query.paginated(patientMasterDataApi.listCasteCategories, {
      queryParams: casteSearch ? { name: casteSearch } : {},
    }),
  });
  const nationalitiesQuery = useQuery({
    queryKey: ["nationalities", nationalitySearch],
    queryFn: query.paginated(patientMasterDataApi.listNationalities, {
      queryParams: nationalitySearch ? { name: nationalitySearch } : {},
    }),
  });

  const religionOptions =
    religionsQuery.data?.results.map((item) => ({
      label: item.name,
      value: item.id,
    })) || [];
  const casteOptions =
    casteQuery.data?.results.map((item) => ({
      label: item.name,
      value: item.id,
    })) || [];
  const nationalityOptions =
    nationalitiesQuery.data?.results.map((item) => ({
      label: item.name,
      value: item.id,
    })) || [];

  const nationalityValue = form.watch("nationality");
  const initialNationalityLabel = form.watch("initial_nationality_label");
  const selectedNationalityOption =
    nationalityValue && initialNationalityLabel
      ? {
          label: initialNationalityLabel,
          value: nationalityValue,
        }
      : undefined;
  const nationalityAutocompleteOptions =
    selectedNationalityOption &&
    !nationalityOptions.some(
      ({ value }) => value === selectedNationalityOption.value,
    )
      ? [selectedNationalityOption, ...nationalityOptions]
      : nationalityOptions;

  const selectedReligion = religionsQuery.data?.results.find(
    (item) => item.id === form.watch("religion"),
  );
  const selectedCaste = casteQuery.data?.results.find(
    (item) => item.id === form.watch("caste_category"),
  );

  const handleEmergencyFieldBlur = () => {
    triggerEmergencyContactValidation(form, t);
  };

  const emergencyContactPersonName = form
    .watch("emergency_contact_person_name")
    ?.trim();

  const emergencyContactPhone = form.watch("emergency_phone_number")?.trim();

  const emergencyContactRelationship = form.watch(
    "emergency_contact_relationship",
  );

  const emergencyContactOther = form
    .watch("emergency_contact_relationship_other")
    ?.trim();

  const emergencyContactHasValue = hasAnyEmergencyContactValue({
    emergency_contact_person_name: emergencyContactPersonName,
    emergency_phone_number: emergencyContactPhone,
    emergency_contact_relationship: emergencyContactRelationship,
  });

  const emergencyContactPersonRequired =
    emergencyContactHasValue && !emergencyContactPersonName;

  const emergencyContactPhoneRequired =
    emergencyContactHasValue && !emergencyContactPhone;

  const emergencyContactRelationshipRequired =
    emergencyContactHasValue && !emergencyContactRelationship;

  const emergencyContactOtherRequired =
    emergencyContactRelationship === "other" && !emergencyContactOther;

  const validateIdentifierField = (
    path: Path<PatientRegistrationFormValues>,
    identifierConfig: PatientIdentifierConfig | undefined,
    value?: string,
    requiredWhenEmpty = false,
  ) => {
    const normalizedValue = normalizeIdentifierValue(identifierConfig, value);
    const pattern = getIdentifierValidationPattern(identifierConfig);

    if (!normalizedValue) {
      if (requiredWhenEmpty) {
        form.setError(path, {
          type: "manual",
          message: t("field_required"),
        });
        return false;
      }
      form.clearErrors(path);
      return true;
    }

    if (!pattern) {
      form.clearErrors(path);
      return true;
    }

    try {
      if (new RegExp(pattern).test(normalizedValue)) {
        form.clearErrors(path);
        return true;
      }
    } catch {
      form.clearErrors(path);
      return true;
    }

    form.setError(path, {
      type: "manual",
      message: `${identifierConfig?.config.display ?? "Identifier"} format is invalid.`,
    });
    return false;
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <FormField
          control={form.control}
          name="emergency_contact_person_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel aria-required={emergencyContactPersonRequired}>
                {t("emergency_contact_person_name")}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ""}
                  placeholder={t("enter_emergency_contact_person_name")}
                  onChange={(event) => {
                    field.onChange(event);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    handleEmergencyFieldBlur();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="emergency_phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel aria-required={emergencyContactPhoneRequired}>
                {t("emergency_contact_number")}
              </FormLabel>
              <FormControl>
                <PhoneInput
                  {...field}
                  placeholder={t("enter_phone_number")}
                  onChange={(value) => {
                    field.onChange(value ?? "");
                  }}
                  onBlur={() => {
                    field.onBlur();
                    handleEmergencyFieldBlur();
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="emergency_contact_relationship"
          render={({ field }) => (
            <FormItem>
              <FormLabel aria-required={emergencyContactRelationshipRequired}>
                {t("emergency_contact_relationship")}
              </FormLabel>
              <Select
                value={field.value ?? ""}
                onValueChange={(value) => {
                  field.onChange(
                    value === "__unset__"
                      ? undefined
                      : (value as EmergencyContactRelationship),
                  );
                  if (value !== "other") {
                    form.setValue("emergency_contact_relationship_other", "", {
                      shouldDirty: true,
                    });
                    form.clearErrors("emergency_contact_relationship_other");
                  }
                  window.setTimeout(() => {
                    triggerEmergencyContactValidation(form, t, {
                      showMissing: value === "other",
                    });
                  }, 0);
                }}
              >
                <FormControl>
                  <SelectTrigger ref={field.ref}>
                    <SelectValue
                      placeholder={t("select_emergency_contact_relationship")}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="__unset__">
                    {t("not_specified")}
                  </SelectItem>
                  {EMERGENCY_RELATIONSHIP_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.watch("emergency_contact_relationship") === "other" && (
          <FormField
            control={form.control}
            name="emergency_contact_relationship_other"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  aria-required={emergencyContactOtherRequired}
                  className="data-[error=true]:text-gray-950"
                >
                  {t("specify_emergency_contact_relationship")}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder={t("specify_emergency_contact_relationship")}
                    onChange={(event) => {
                      const sanitized = sanitizeAlphabeticInput(
                        event.target.value,
                      );
                      field.onChange(sanitized);
                      if (sanitized.trim()) {
                        validateAlphabeticOtherField(
                          form,
                          "emergency_contact_relationship_other",
                          sanitized,
                          t,
                          true,
                        );
                      } else {
                        form.clearErrors(
                          "emergency_contact_relationship_other",
                        );
                      }
                    }}
                    onBlur={() => {
                      field.onBlur();
                      validateAlphabeticOtherField(
                        form,
                        "emergency_contact_relationship_other",
                        field.value,
                        t,
                        true,
                      );
                      handleEmergencyFieldBlur();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {isCreate && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("patient_tags")}</FormLabel>
                <FormControl>
                  <TagSelectorPopover
                    facilityId={facilityId}
                    selected={selectedTags}
                    onChange={(tags) => {
                      field.onChange(tags.map((tag) => tag.id));
                    }}
                    resource={TagResource.PATIENT}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {facility &&
        getRequiredIdentifierConfigs(facility).map(({ id, config }, idx) => (
          <FormField
            key={id}
            control={form.control}
            name={`required_identifiers.${idx}.value`}
            render={({ field }) => (
              <FormItem>
                <FormLabel aria-required>{config.display}</FormLabel>
                <FormDescription>{config.description}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder={t("enter_identifier_value")}
                    onBlur={() => {
                      field.onBlur();
                      const path = `required_identifiers.${idx}.value` as const;
                      const value = (field.value ?? "").trim();
                      const pattern = getIdentifierValidationPattern({
                        id,
                        config,
                      } as PatientIdentifierConfig);

                      if (!value || !pattern) {
                        form.clearErrors(path);
                        return;
                      }

                      try {
                        if (new RegExp(pattern).test(value)) {
                          form.clearErrors(path);
                        } else {
                          form.setError(path, {
                            type: "manual",
                            message: `${config.display} format is invalid.`,
                          });
                        }
                      } catch {
                        form.clearErrors(path);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Tab" && event.key !== "Enter") {
                        return;
                      }

                      const path = `required_identifiers.${idx}.value` as const;
                      const value = event.currentTarget.value.trim();
                      const pattern = getIdentifierValidationPattern({
                        id,
                        config,
                      } as PatientIdentifierConfig);

                      if (!value || !pattern) {
                        form.clearErrors(path);
                        return;
                      }

                      try {
                        if (new RegExp(pattern).test(value)) {
                          form.clearErrors(path);
                        } else {
                          form.setError(path, {
                            type: "manual",
                            message: `${config.display} format is invalid.`,
                          });
                        }
                      } catch {
                        form.clearErrors(path);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}

      <div className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("address")}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t("type_address")}
                  onChange={(e) => {
                    field.onChange(e);
                    if (form.getValues("permanent_address_same_as_address")) {
                      form.setValue("permanent_address", e.target.value, {
                        shouldDirty: true,
                      });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="permanent_address_same_as_address"
          render={({ field }) => (
            <FormItem className="flex items-center gap-2">
              <FormControl>
                <Checkbox
                  checked={!field.value}
                  onCheckedChange={(value) => field.onChange(!value)}
                />
              </FormControl>
              <FormLabel>
                {t("permanent_address_is_different_from_current_address")}
              </FormLabel>
            </FormItem>
          )}
        />
      </div>

      {!form.watch("permanent_address_same_as_address") && (
        <FormField
          control={form.control}
          name="permanent_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("permanent_address")}</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t("type_address")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="pincode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("pincode")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ""}
                  placeholder={t("enter_pincode")}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value.startsWith("0")) {
                      form.setError("pincode", {
                        type: "manual",
                        message: t("pincode_must_not_start_with_0"),
                      });
                    } else {
                      form.clearErrors("pincode");
                    }

                    field.onChange(value ? Number(value) : undefined);
                  }}
                  onBlur={() => {
                    field.onBlur();

                    const pincodeValue = field.value;

                    if (pincodeValue === undefined || pincodeValue === null) {
                      form.clearErrors("pincode");
                      return;
                    }

                    void form.trigger("pincode");
                  }}
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {primaryIdentityConfigs.length > 0 && selectedPrimaryIdentityConfig && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="government_identifier_config"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("id_type")}</FormLabel>
                  <Select
                    value={field.value ?? NOT_SPECIFIED_ID}
                    onValueChange={(value) => {
                      field.onChange(value);
                      form.setValue("government_identifier_value", "", {
                        shouldDirty: true,
                      });
                      form.setValue(
                        "government_identifier_document_file",
                        undefined,
                        {
                          shouldDirty: true,
                        },
                      );
                      form.setValue(
                        "government_identifier_document_pending",
                        null,
                        {
                          shouldDirty: true,
                        },
                      );
                      form.clearErrors("government_identifier_value");
                      form.clearErrors("government_identifier_document_file");
                    }}
                  >
                    <FormControl>
                      <SelectTrigger ref={field.ref}>
                        <SelectValue placeholder={t("select_id_type")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NOT_SPECIFIED_ID}>
                        {t("not_specified")}
                      </SelectItem>
                      {primaryIdentityConfigs.map(({ id, config }) => (
                        <SelectItem key={id} value={id}>
                          {config.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="government_identifier_document_file"
              render={() => (
                <PatientRegistrationFileField
                  label={t("id_proof_document")}
                  patientId={patientId}
                  value={form.watch("government_identifier_document_file")}
                  pendingFile={form.watch(
                    "government_identifier_document_pending",
                  )}
                  allowDownload={
                    !!patientId &&
                    !!form.watch("government_identifier_document_file")?.id &&
                    !form.formState.dirtyFields
                      .government_identifier_document_file
                  }
                  onChange={({ fileId, fileRecord, pendingFile }) => {
                    form.setValue(
                      "government_identifier_document_file",
                      fileRecord || undefined,
                      { shouldDirty: true },
                    );
                    form.setValue(
                      "government_identifier_document_pending",
                      pendingFile || null,
                      { shouldDirty: true },
                    );
                    if (fileId) {
                      form.clearErrors("government_identifier_document_file");
                    }
                    void form.trigger("government_identifier_value");
                  }}
                />
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="government_identifier_value"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  aria-required={
                    !!form.watch("government_identifier_document_file")?.id ||
                    !!form.watch("government_identifier_document_pending")
                  }
                >
                  {normalizeRequiredLabel(
                    selectedPrimaryIdentityConfig.config.display,
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder={t("enter_identifier_value")}
                    onChange={(event) => {
                      field.onChange(event);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Tab" && event.key !== "Enter") {
                        return;
                      }
                      validateIdentifierField(
                        "government_identifier_value",
                        selectedPrimaryIdentityConfig,
                        event.currentTarget.value,
                        !!form.watch("government_identifier_document_file")
                          ?.id ||
                          !!form.watch(
                            "government_identifier_document_pending",
                          ),
                      );
                    }}
                    onBlur={() => {
                      field.onBlur();
                      const normalizedValue = normalizeIdentifierValue(
                        selectedPrimaryIdentityConfig,
                        field.value,
                      );
                      form.setValue(
                        "government_identifier_value",
                        normalizedValue,
                        { shouldDirty: true },
                      );
                      validateIdentifierField(
                        "government_identifier_value",
                        selectedPrimaryIdentityConfig,
                        normalizedValue,
                        !!form.watch("government_identifier_document_file")
                          ?.id ||
                          !!form.watch(
                            "government_identifier_document_pending",
                          ),
                      );
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {primaryIdentityConfigs.length > 0 && !selectedPrimaryIdentityConfig && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="government_identifier_config"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("id_type")}</FormLabel>
                <Select
                  value={field.value ?? NOT_SPECIFIED_ID}
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.setValue("government_identifier_value", "", {
                      shouldDirty: true,
                    });
                    form.setValue(
                      "government_identifier_document_file",
                      undefined,
                      {
                        shouldDirty: true,
                      },
                    );
                    form.setValue(
                      "government_identifier_document_pending",
                      null,
                      {
                        shouldDirty: true,
                      },
                    );
                    form.clearErrors("government_identifier_value");
                    form.clearErrors("government_identifier_document_file");
                  }}
                >
                  <FormControl>
                    <SelectTrigger ref={field.ref}>
                      <SelectValue placeholder={t("select_id_type")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={NOT_SPECIFIED_ID}>
                      {t("not_specified")}
                    </SelectItem>
                    {primaryIdentityConfigs.map(({ id, config }) => (
                      <SelectItem key={id} value={id}>
                        {config.display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {facility &&
        getNonGovernmentOptionalIdentifierConfigs(facility).map(
          ({ id, config }, idx) => {
            const isAbha = `${config.display} ${config.system}`
              .toLowerCase()
              .includes("abha");

            if (isAbha) {
              return (
                <div key={id} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`optional_identifiers.${idx}.value`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          aria-required={
                            !!form.watch("abha_id_document_file")?.id ||
                            !!form.watch("abha_id_document_pending")
                          }
                        >
                          {normalizeRequiredLabel(config.display)}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            placeholder={t("enter_identifier_value")}
                            onChange={(event) => {
                              field.onChange(event);
                            }}
                            onKeyDown={(event) => {
                              if (
                                event.key !== "Tab" &&
                                event.key !== "Enter"
                              ) {
                                return;
                              }
                              validateIdentifierField(
                                `optional_identifiers.${idx}.value` as Path<PatientRegistrationFormValues>,
                                { id, config } as PatientIdentifierConfig,
                                event.currentTarget.value,
                                !!form.watch("abha_id_document_file")?.id ||
                                  !!form.watch("abha_id_document_pending"),
                              );
                            }}
                            onBlur={() => {
                              field.onBlur();
                              validateIdentifierField(
                                `optional_identifiers.${idx}.value` as Path<PatientRegistrationFormValues>,
                                { id, config } as PatientIdentifierConfig,
                                field.value,
                                !!form.watch("abha_id_document_file")?.id ||
                                  !!form.watch("abha_id_document_pending"),
                              );
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="abha_id_document_file"
                    render={() => (
                      <div className="w-full min-w-0">
                        <PatientRegistrationFileField
                          label={t("abha_id_document")}
                          required={
                            !!form.watch("abha_id_document_file")?.id ||
                            !!form.watch("abha_id_document_pending")
                          }
                          patientId={patientId}
                          value={form.watch("abha_id_document_file")}
                          pendingFile={form.watch("abha_id_document_pending")}
                          allowDownload={
                            !!patientId &&
                            !!form.watch("abha_id_document_file")?.id &&
                            !form.formState.dirtyFields.abha_id_document_file
                          }
                          onChange={({ fileRecord, pendingFile }) => {
                            form.setValue(
                              "abha_id_document_file",
                              fileRecord || undefined,
                              { shouldDirty: true },
                            );
                            form.setValue(
                              "abha_id_document_pending",
                              pendingFile || null,
                              { shouldDirty: true },
                            );
                            void form.trigger(
                              `optional_identifiers.${idx}.value`,
                            );
                          }}
                        />
                      </div>
                    )}
                  />
                </div>
              );
            }

            return (
              <FormField
                key={id}
                control={form.control}
                name={`optional_identifiers.${idx}.value`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{config.display}</FormLabel>
                    <FormDescription>{config.description}</FormDescription>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder={t("enter_identifier_value")}
                        onBlur={() => {
                          field.onBlur();
                          const path =
                            `optional_identifiers.${idx}.value` as const;
                          const value = (field.value ?? "").trim();
                          const pattern = getIdentifierValidationPattern({
                            id,
                            config,
                          } as PatientIdentifierConfig);

                          if (!value || !pattern) {
                            form.clearErrors(path);
                            return;
                          }

                          try {
                            if (new RegExp(pattern).test(value)) {
                              form.clearErrors(path);
                            } else {
                              form.setError(path, {
                                type: "manual",
                                message: `${config.display} format is invalid.`,
                              });
                            }
                          } catch {
                            form.clearErrors(path);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            );
          },
        )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="patient_photo_file"
          render={({ field: _field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel>{t("patient_photo")}</FormLabel>
              <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
                <div className="min-w-0 flex-1">
                  <PatientRegistrationFileField
                    showLabel={false}
                    showViewDownload={false}
                    allowDownload={
                      !!patientId &&
                      !!form.watch("patient_photo_file")?.id &&
                      !form.formState.dirtyFields.patient_photo_file
                    }
                    patientId={patientId}
                    value={form.watch("patient_photo_file")}
                    pendingFile={form.watch("patient_photo_pending")}
                    allowedExtensions={["jpg", "jpeg", "png"]}
                    maxSizeMB={PATIENT_REGISTRATION_PHOTO_MAX_SIZE_MB}
                    onChange={({ fileRecord, pendingFile }) => {
                      form.setValue(
                        "patient_photo_file",
                        fileRecord || undefined,
                        { shouldDirty: true },
                      );
                      form.setValue(
                        "patient_photo_pending",
                        pendingFile || null,
                        {
                          shouldDirty: true,
                        },
                      );
                      if (pendingFile) {
                        const reader = new FileReader();
                        reader.onload = () => {
                          form.setValue(
                            "patient_photo_preview",
                            reader.result as string,
                            { shouldDirty: false },
                          );
                        };
                        reader.readAsDataURL(pendingFile);
                      } else if (!fileRecord) {
                        form.setValue("patient_photo_preview", "", {
                          shouldDirty: false,
                        });
                      }
                    }}
                  />
                </div>
                <PatientPhotoPreview form={form} patientId={patientId} />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="religion"
          render={({ field }) => (
            <FormItem className="h-fit">
              <FormLabel>{t("religion")}</FormLabel>
              <FormControl>
                <Autocomplete
                  value={field.value ?? ""}
                  onChange={(value) => {
                    field.onChange(value);
                    setReligionSearch("");
                    const next = religionsQuery.data?.results.find(
                      (item) => item.id === value,
                    );
                    if (!next?.is_other) {
                      form.setValue("religion_other", "", {
                        shouldDirty: true,
                      });
                      form.clearErrors("religion_other");
                    } else {
                      void form.trigger("religion_other");
                    }
                  }}
                  onSearch={setReligionSearch}
                  options={religionOptions}
                  isLoading={religionsQuery.isLoading}
                  placeholder={t("select_religion")}
                  inputPlaceholder={t("search_religion")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {selectedReligion?.is_other && (
          <FormField
            control={form.control}
            name="religion_other"
            render={({ field }) => (
              <FormItem className="h-fit">
                <FormLabel aria-required>{t("specify_religion")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder={t("specify_religion")}
                    onChange={(event) => {
                      const sanitized = sanitizeAlphabeticInput(
                        event.target.value,
                      );
                      field.onChange(sanitized);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      validateAlphabeticOtherField(
                        form,
                        "religion_other",
                        field.value,
                        t,
                        true,
                      );
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="caste_category"
          render={({ field }) => (
            <FormItem className="h-fit">
              <FormLabel>{t("caste_category")}</FormLabel>
              <FormControl>
                <Autocomplete
                  value={field.value ?? ""}
                  onChange={(value) => {
                    field.onChange(value);
                    setCasteSearch("");
                    const next = casteQuery.data?.results.find(
                      (item) => item.id === value,
                    );
                    if (!next?.is_other) {
                      form.setValue("caste_category_other", "", {
                        shouldDirty: true,
                      });
                      form.clearErrors("caste_category_other");
                    } else {
                      void form.trigger("caste_category_other");
                    }
                  }}
                  onSearch={setCasteSearch}
                  options={casteOptions}
                  isLoading={casteQuery.isLoading}
                  placeholder={t("select_caste_category")}
                  inputPlaceholder={t("search_caste_category")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {selectedCaste?.is_other && (
          <FormField
            control={form.control}
            name="caste_category_other"
            render={({ field }) => (
              <FormItem className="h-fit">
                <FormLabel aria-required>
                  {t("specify_caste_category")}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder={t("specify_caste_category")}
                    onChange={(event) => {
                      const sanitized = sanitizeAlphanumericInput(
                        event.target.value,
                      );
                      field.onChange(sanitized);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      validateAlphanumericOtherField(
                        form,
                        "caste_category_other",
                        field.value,
                        t,
                        true,
                      );
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="bpl_apl_category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("bpl_apl_category")}</FormLabel>
              <Select
                value={field.value ?? ""}
                onValueChange={(value) =>
                  field.onChange(value === NOT_SPECIFIED_ID ? undefined : value)
                }
              >
                <FormControl>
                  <SelectTrigger ref={field.ref}>
                    <SelectValue placeholder={t("select_bpl_apl_category")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NOT_SPECIFIED_ID}>
                    {t("not_specified")}
                  </SelectItem>
                  {BPL_APL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="occupation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("occupation")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ""}
                  placeholder={t("enter_occupation")}
                  onChange={(event) => {
                    const sanitized = sanitizeAlphanumericInput(
                      event.target.value,
                    );
                    field.onChange(sanitized);
                    if (sanitized.trim()) {
                      form.clearErrors("occupation");
                    }
                  }}
                  onBlur={() => {
                    field.onBlur();
                    const trimmed = (field.value ?? "").trim();
                    if (!trimmed) {
                      form.clearErrors("occupation");
                      return;
                    }
                    if (!ALPHANUMERIC_ONLY_PATTERN.test(trimmed)) {
                      form.setError("occupation", {
                        type: "manual",
                        message: t("alphanumeric_characters_only"),
                      });
                    } else {
                      form.clearErrors("occupation");
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="nationality"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("nationality")}</FormLabel>
              <FormControl>
                <Autocomplete
                  value={field.value ?? ""}
                  onChange={(value) => {
                    field.onChange(value);
                    setNationalitySearch("");
                  }}
                  onSearch={setNationalitySearch}
                  options={nationalityAutocompleteOptions}
                  isLoading={nationalitiesQuery.isLoading}
                  placeholder={t("select_nationality")}
                  inputPlaceholder={t("search_nationality")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {facility &&
        getAutogeneratedIdentifierConfigs(facility).map(
          ({ id, config }, idx) => (
            <FormField
              key={id}
              control={form.control}
              name={`autogenerated_identifiers.${idx}.value`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{config.display}</FormLabel>
                  <FormDescription>{config.description}</FormDescription>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("identifier_value_autogenerated")}
                      disabled
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ),
        )}
    </>
  );
};

const getIdentifierSortPriority = ({ config }: PatientIdentifierConfig) => {
  const identifierText = `${config.display} ${config.system}`.toLowerCase();

  if (identifierText.includes("abha")) {
    return 0;
  }

  if (identifierText.includes("aadhaar") || identifierText.includes("aadhar")) {
    return 1;
  }

  return 2;
};

const sortIdentifierConfigs = (configs: PatientIdentifierConfig[]) => {
  return [...configs].sort(
    (a, b) => getIdentifierSortPriority(a) - getIdentifierSortPriority(b),
  );
};

const getRequiredIdentifierConfigs = (facility: FacilityRead) => {
  const configs = facility.patient_instance_identifier_configs;
  return configs.filter(({ config }) => {
    if (config.auto_maintained) {
      return false;
    }
    const isAutogenerated = !!config.default_value;
    const isRequired = config.required && !isAutogenerated;
    return isRequired;
  });
};

const getOptionalIdentifierConfigs = (facility: FacilityRead) => {
  const configs = facility.patient_instance_identifier_configs;
  return sortIdentifierConfigs(
    configs.filter(({ config }) => {
      if (config.auto_maintained) {
        return false;
      }
      const isAutogenerated = !!config.default_value;
      const isOptional = !config.required && !isAutogenerated;
      return isOptional;
    }),
  );
};

const isAadhaarIdentifier = ({ config }: PatientIdentifierConfig) => {
  const identifierText = `${config.display} ${config.system}`.toLowerCase();
  return ["aadhaar", "aadhar"].some((keyword) =>
    identifierText.includes(keyword),
  );
};

const isGovernmentDropdownIdentifier = ({
  config,
}: PatientIdentifierConfig) => {
  const identifierText = `${config.display} ${config.system}`.toLowerCase();
  return [
    "pan",
    "voter",
    "epic",
    "passport",
    "driving licence",
    "driving license",
    "ration",
  ].some((keyword) => identifierText.includes(keyword));
};

const getGovernmentIdentifierConfigs = (facility: FacilityRead) => {
  return getOptionalIdentifierConfigs(facility).filter(
    isGovernmentDropdownIdentifier,
  );
};

const getAadhaarIdentifierConfig = (facility: FacilityRead) => {
  return getOptionalIdentifierConfigs(facility).find(isAadhaarIdentifier);
};

const getPrimaryIdentityDropdownConfigs = (facility: FacilityRead) => {
  const aadhaarConfig = getAadhaarIdentifierConfig(facility);
  const governmentConfigs = getGovernmentIdentifierConfigs(facility);

  return aadhaarConfig
    ? [aadhaarConfig, ...governmentConfigs]
    : governmentConfigs;
};

const getNonGovernmentOptionalIdentifierConfigs = (facility: FacilityRead) => {
  return getOptionalIdentifierConfigs(facility).filter(
    (config) =>
      !isGovernmentDropdownIdentifier(config) && !isAadhaarIdentifier(config),
  );
};

const getAutogeneratedIdentifierConfigs = (facility: FacilityRead) => {
  const configs = facility.patient_instance_identifier_configs;
  return configs.filter(({ config }) => {
    if (config.auto_maintained) {
      return false;
    }
    const isAutogenerated = !!config.default_value;
    return isAutogenerated;
  });
};

const geoOrgValidator = (t: TFunction) => {
  const requiredLevels =
    careConfig.patientRegistration.minGeoOrganizationLevelsRequired;

  if (!requiredLevels) {
    return z.string().uuid({ message: t("geo_organization_is_required") });
  }

  return z.string().uuid({
    message: t("govt_organization_required_depth_validation", {
      depth: requiredLevels,
    }),
  });
};

const getFormSchema = (t: TFunction, facility?: FacilityRead) => {
  return z
    .object({
      // Patient Basics
      name: z.string().trim().optional(),
      phone_number: validators().phoneNumber.required,
      alternate_phone_number: validators().phoneNumber.optional,
      email: z
        .string()
        .trim()
        .optional()
        .refine((val) => !val || z.string().email().safeParse(val).success, {
          message: t("invalid_email"),
        }),
      preferred_language: z.string().trim().max(100).optional(),
      emergency_phone_number: validators().phoneNumber.optional,
      emergency_contact_person_name: z.string().trim().optional(),
      emergency_contact_relationship: z
        .enum([
          "spouse",
          "parent",
          "child",
          "sibling",
          "friend",
          "guardian",
          "other",
        ])
        .optional(),
      emergency_contact_relationship_other: z.string().trim().optional(),
      gender: z.enum(PATIENT_GENDERS).optional(),
      age_or_dob: z.enum(["dob", "age"]),
      date_of_birth: z
        .string()
        .date()
        .refine((date) => !isFuture(date), t("date_cannot_be_future"))
        .refine((date) => {
          if (!date) return true;

          const [year, month, day] = date.split("-").map(Number);
          const dob = new Date(year, month - 1, day);
          const today = new Date();

          let age = today.getFullYear() - year;

          const monthDiff = today.getMonth() - dob.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
          ) {
            age--;
          }

          return age <= 130;
        }, t("age_must_be_below_130"))
        .optional()
        .nullable(),
      age: validators().age.optional().nullable(),
      blood_group: z.nativeEnum(BloodGroupChoices).optional(),
      tags: z.array(z.string()),
      relationship_type: z.enum(["father", "mother", "spouse"]).optional(),
      insurance_records: z.array(insuranceSchema).optional(),
      relationship_name: z.string().trim().optional(),
      marital_status: z
        .enum(["single", "married", "divorced", "widowed", "separated"])
        .optional(),

      // Non-mandatory details
      address: z.string().trim().optional(),
      permanent_address: z.string().trim().optional(),
      permanent_address_same_as_address: z.boolean(),
      geo_organization: geoOrgValidator(t),
      pincode: validators().pincode.optional(),

      religion: z.string().uuid().optional().or(z.literal("")),
      religion_other: z.string().trim().optional(),
      caste_category: z.string().uuid().optional().or(z.literal("")),
      caste_category_other: z.string().trim().optional(),
      bpl_apl_category: z.enum(["bpl", "apl", "not_applicable"]).optional(),
      occupation: z.string().trim().optional(),
      nationality: z.string().uuid().optional().or(z.literal("")),
      patient_photo_file: z.custom<PatientFileRecord | undefined>().optional(),
      patient_photo_pending: z.custom<File | null>().optional().nullable(),
      patient_photo_preview: z.string().optional(),
      government_identifier_document_file: z
        .custom<PatientFileRecord | undefined>()
        .optional(),
      government_identifier_document_pending: z
        .custom<File | null>()
        .optional()
        .nullable(),
      abha_id_document_file: z
        .custom<PatientFileRecord | undefined>()
        .optional(),
      abha_id_document_pending: z.custom<File | null>().optional().nullable(),

      // Government ID identifier fields
      government_identifier_config: z.string().optional(),
      government_identifier_value: z.string().optional(),
      initial_government_identifier_config: z.string().uuid().optional(),
      // Identifier related fields
      required_identifiers: z.array(
        z.object({
          config: z.string().uuid(),
          value: z.string().nonempty(t("field_required")),
        }),
      ),
      optional_identifiers: z.array(
        z.object({
          config: z.string().uuid(),
          value: z.string().optional(),
        }),
      ),
      autogenerated_identifiers: z.array(
        z.object({ config: z.string().uuid(), value: z.string().optional() }),
      ),
      // Store initial identifier values so we can detect clears on update
      initial_required_identifiers: z
        .array(
          z.object({ config: z.string().uuid(), value: z.string().optional() }),
        )
        .optional(),
      initial_optional_identifiers: z
        .array(
          z.object({ config: z.string().uuid(), value: z.string().optional() }),
        )
        .optional(),
      initial_autogenerated_identifiers: z
        .array(
          z.object({ config: z.string().uuid(), value: z.string().optional() }),
        )
        .optional(),

      // Track initial values for optional fields so clearing them will be
      // represented in update payloads.
      initial_alternate_phone_number: z.string().optional(),
      initial_email: z.string().optional(),
      initial_emergency_phone_number: z.string().optional(),
      initial_emergency_contact_person_name: z.string().optional(),
      initial_emergency_contact_relationship: z
        .enum([
          "spouse",
          "parent",
          "child",
          "sibling",
          "friend",
          "guardian",
          "other",
        ])
        .optional(),
      initial_emergency_contact_relationship_other: z.string().optional(),

      initial_religion: z
        .string()
        .uuid()
        .optional()
        .or(z.literal(""))
        .optional(),
      initial_caste_category: z
        .string()
        .uuid()
        .optional()
        .or(z.literal(""))
        .optional(),
      initial_occupation: z.string().optional(),
      initial_nationality: z
        .string()
        .uuid()
        .optional()
        .or(z.literal(""))
        .optional(),
      initial_nationality_label: z.string().optional(),

      // Track presence of uploaded files by storing their original file ids
      initial_government_identifier_document_file_id: z
        .string()
        .optional()
        .nullable(),
      initial_abha_id_document_file_id: z.string().optional().nullable(),
      initial_patient_photo_file_id: z.string().optional().nullable(),

      // Meta fields used for internal state management
      _selected_levels: z.array(z.custom<Organization>()),
    })
    .superRefine((data, ctx) => {
      const validateIdentifierPatterns = (
        identifiers: { config: string; value?: string }[],
        path:
          | "required_identifiers"
          | "optional_identifiers"
          | "government_identifier_value",
      ) => {
        if (!facility) {
          return;
        }

        identifiers.forEach((identifier, idx) => {
          const config = facility.patient_instance_identifier_configs.find(
            (c) => c.id === identifier.config,
          );
          const value = normalizeIdentifierValue(config, identifier.value);
          if (!value) {
            return;
          }

          const pattern = getIdentifierValidationPattern(config);

          if (!pattern) {
            return;
          }

          let isMatch = true;
          try {
            isMatch = new RegExp(pattern).test(value);
          } catch {
            return;
          }

          if (!isMatch) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${config?.config.display ?? "Identifier"} format is invalid.`,
              path:
                path === "government_identifier_value"
                  ? ["government_identifier_value"]
                  : [path, idx, "value"],
            });
          }
        });
      };

      const emergencyPersonName = data.emergency_contact_person_name?.trim();
      const emergencyRelationship = data.emergency_contact_relationship;
      const emergencyPhone = data.emergency_phone_number?.trim();
      const emergencyFields = [
        emergencyPersonName,
        emergencyRelationship,
        emergencyPhone,
      ];

      if (emergencyFields.some(Boolean)) {
        if (!emergencyPersonName) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emergency_contact_person_name"],
            message: t("field_required"),
          });
        }
        if (!emergencyRelationship) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emergency_contact_relationship"],
            message: t("field_required"),
          });
        }
        if (!emergencyPhone) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emergency_phone_number"],
            message: t("field_required"),
          });
        }
      }

      if (
        emergencyPhone &&
        data.phone_number &&
        emergencyPhone === data.phone_number
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["emergency_phone_number"],
          message: t("emergency_phone_number_must_differ"),
        });
      }

      if (emergencyRelationship === "other") {
        const other = data.emergency_contact_relationship_other?.trim() || "";
        if (!other) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emergency_contact_relationship_other"],
            message: t("field_required"),
          });
        } else if (!ALPHABETIC_ONLY_PATTERN.test(other)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emergency_contact_relationship_other"],
            message: t("alphabetic_characters_only"),
          });
        }
      }

      const religionOther = data.religion_other?.trim() || "";
      if (religionOther && !ALPHABETIC_ONLY_PATTERN.test(religionOther)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["religion_other"],
          message: t("alphabetic_characters_only"),
        });
      }

      const casteOther = data.caste_category_other?.trim() || "";
      if (casteOther && !ALPHANUMERIC_ONLY_PATTERN.test(casteOther)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["caste_category_other"],
          message: t("alphanumeric_characters_only"),
        });
      }

      // Validate the date of birth / age.
      if (data.age_or_dob === "dob" && !data.date_of_birth) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("field_required"),
          path: ["age_or_dob"],
        });
      }
      if (data.age_or_dob === "age" && !data.age) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("field_required"),
          path: ["age_or_dob"],
        });
      }

      if (!data.relationship_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("field_required"),
          path: ["relationship_name"],
        });
      }

      validateIdentifierPatterns(
        data.required_identifiers,
        "required_identifiers",
      );
      validateIdentifierPatterns(
        data.optional_identifiers,
        "optional_identifiers",
      );
      if (
        data.government_identifier_config &&
        data.government_identifier_config !== NOT_SPECIFIED_ID
      ) {
        validateIdentifierPatterns(
          [
            {
              config: data.government_identifier_config,
              value: data.government_identifier_value,
            },
          ],
          "government_identifier_value",
        );

        const hasDocument =
          !!data.government_identifier_document_file?.id ||
          !!data.government_identifier_document_pending;
        const hasValue = !!data.government_identifier_value?.trim();

        if (hasDocument && !hasValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["government_identifier_value"],
            message: t("field_required"),
          });
        }
      }

      if (facility) {
        getNonGovernmentOptionalIdentifierConfigs(facility).forEach(
          ({ config }, idx) => {
            const identifierText =
              `${config.display} ${config.system}`.toLowerCase();
            if (!identifierText.includes("abha")) {
              return;
            }

            const value = data.optional_identifiers[idx]?.value?.trim();
            const hasDocument =
              !!data.abha_id_document_file?.id ||
              !!data.abha_id_document_pending;

            if (hasDocument && !value) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["optional_identifiers", idx, "value"],
                message: t("field_required"),
              });
            }
          },
        );
      }
    });
};

export type PatientRegistrationFormValues = z.infer<
  ReturnType<typeof getFormSchema>
>;

type MandatoryPatientBasics = {
  name: string;
  gender: NonNullable<PatientRegistrationFormValues["gender"]>;
  relationship_type: NonNullable<
    PatientRegistrationFormValues["relationship_type"]
  >;
  relationship_name: string;
};

const normalizeRequiredLabel = (label: string) =>
  label.replace(/\s*\*+\s*$/, "");

const normalizeIdentifierValue = (
  identifierConfig: PatientIdentifierConfig | undefined,
  value?: string,
) => {
  const normalized = value?.trim() ?? "";
  if (!normalized || !identifierConfig) {
    return normalized;
  }

  const identifierText =
    `${identifierConfig.config.display} ${identifierConfig.config.system}`.toLowerCase();

  if (
    ["aadhaar", "aadhar", "abha"].some((keyword) =>
      identifierText.includes(keyword),
    )
  ) {
    return normalized.replace(/[\s-]+/g, "");
  }

  if (
    ["pan", "voter", "epic", "passport", "ration"].some((keyword) =>
      identifierText.includes(keyword),
    )
  ) {
    return normalized.replace(/\s+/g, "").toUpperCase();
  }

  if (
    ["driving licence", "driving license", "licence", "license"].some(
      (keyword) => identifierText.includes(keyword),
    )
  ) {
    return normalized.replace(/[\s-]+/g, "").toUpperCase();
  }

  return normalized;
};

const getIdentifierValidationPattern = (
  identifierConfig: PatientIdentifierConfig | undefined,
) => {
  const configuredPattern = identifierConfig?.config.regex?.trim();
  if (configuredPattern) {
    return configuredPattern;
  }

  const identifierText =
    `${identifierConfig?.config.display ?? ""} ${identifierConfig?.config.system ?? ""}`.toLowerCase();

  if (
    ["aadhaar", "aadhar"].some((keyword) => identifierText.includes(keyword))
  ) {
    return "^(?:\\d{12}|\\d{16})$";
  }

  if (identifierText.includes("abha")) {
    return "^\\d{14}$";
  }

  if (identifierText.includes("pan")) {
    return "^[A-Z]{5}[0-9]{4}[A-Z]$";
  }

  if (identifierText.includes("voter") || identifierText.includes("epic")) {
    return "^[A-Z]{3}[0-9]{7}$";
  }

  if (identifierText.includes("passport")) {
    return "^[A-Z][0-9]{7}$";
  }

  if (
    ["driving licence", "driving license", "licence", "license"].some(
      (keyword) => identifierText.includes(keyword),
    )
  ) {
    return "^[A-Z]{2}[0-9]{13}$";
  }

  if (identifierText.includes("ration")) {
    return "^[A-Z0-9/]{5,20}$";
  }

  return "";
};

const getPatientIdentifiersPayload = (
  values: PatientRegistrationFormValues,
  facility: FacilityRead,
) => {
  const payload: PatientIdentifierCreate[] = [];

  const sections: {
    current: { config: string; value?: string | undefined }[];
    initial?: { config: string; value?: string | undefined }[] | undefined;
  }[] = [
    {
      current: values.required_identifiers,
      initial: values.initial_required_identifiers,
    },
    {
      current: values.optional_identifiers,
      initial: values.initial_optional_identifiers,
    },
    {
      current: values.autogenerated_identifiers,
      initial: values.initial_autogenerated_identifiers,
    },
  ];

  sections.forEach(({ current, initial }) => {
    current.forEach((identifier, idx) => {
      const configEntry = facility.patient_instance_identifier_configs.find(
        (c) => c.id === identifier.config,
      );
      if (configEntry?.config.auto_maintained) {
        return;
      }
      if (configEntry?.config.default_value) {
        // autogenerated — backend manages this
        return;
      }

      const normalizedValue = normalizeIdentifierValue(
        configEntry,
        identifier.value,
      );

      if (normalizedValue) {
        payload.push({ config: identifier.config, value: normalizedValue });
        return;
      }

      // If there's no current value but there was an initial value, include an empty string to clear it
      const initialValue = initial?.[idx]?.value;
      if (initialValue && initialValue.trim()) {
        payload.push({ config: identifier.config, value: "" });
      }
    });
  });

  const selectedGovernmentConfig =
    values.government_identifier_config === NOT_SPECIFIED_ID
      ? undefined
      : values.government_identifier_config;
  const initialGovernmentConfig = values.initial_government_identifier_config;

  if (selectedGovernmentConfig) {
    const config = facility.patient_instance_identifier_configs.find(
      (c) => c.id === selectedGovernmentConfig,
    );
    const normalizedValue = normalizeIdentifierValue(
      config,
      values.government_identifier_value,
    );

    if (normalizedValue) {
      payload.push({
        config: selectedGovernmentConfig,
        value: normalizedValue,
      });
    } else if (initialGovernmentConfig === selectedGovernmentConfig) {
      payload.push({
        config: selectedGovernmentConfig,
        value: "",
      });
    }
  }

  if (
    initialGovernmentConfig &&
    initialGovernmentConfig !== selectedGovernmentConfig
  ) {
    payload.push({
      config: initialGovernmentConfig,
      value: "",
    });
  }

  return payload;
};

const PatientPhotoPreview = ({
  form,
  patientId,
}: {
  form: UseFormReturn<PatientRegistrationFormValues>;
  patientId?: string;
}) => {
  const { t } = useTranslation();
  const fileManager = useFileManager({ type: FileType.PATIENT });
  const [signedUrl, setSignedUrl] = useState<string>("");
  const photoPreview = form.watch("patient_photo_preview");
  const photoFileRecord = form.watch("patient_photo_file");
  const photoPendingFile = form.watch("patient_photo_pending");

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    const fetchUrl = async () => {
      if (!patientId || !photoFileRecord?.id || photoPendingFile) {
        if (mounted) setSignedUrl("");
        return;
      }

      try {
        const data = await query(fileApi.get, {
          pathParams: { fileId: photoFileRecord.id },
        })({ signal: controller.signal });
        if (mounted) setSignedUrl((data as any)?.read_signed_url ?? "");
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Failed to fetch patient photo URL", err);
        if (mounted) setSignedUrl("");
      }
    };

    void fetchUrl();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [patientId, photoFileRecord?.id, photoPendingFile]);

  if (
    !photoPreview &&
    !(!!patientId && !!photoFileRecord?.id && !photoPendingFile)
  ) {
    return null;
  }

  return (
    <>
      <div className="flex shrink-0 items-center justify-start gap-2">
        <img
          src={photoPreview || signedUrl || ""}
          alt={t("patient_photo")}
          className="size-16 rounded-full border border-gray-300 object-cover"
        />
        {photoPendingFile ? (
          <button
            type="button"
            className="inline-flex h-8 items-center rounded-md px-2 text-sm font-medium text-primary hover:bg-primary-50"
            onClick={() => {
              const url = URL.createObjectURL(photoPendingFile);
              window.open(url, "_blank", "noopener,noreferrer");
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
          >
            {t("view")}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="inline-flex h-8 items-center rounded-md px-2 text-sm font-medium text-primary hover:bg-primary-50"
              onClick={() => {
                if (photoFileRecord?.id && patientId) {
                  const fileForActions: FileReadMinimal = {
                    id: photoFileRecord.id,
                    name: photoFileRecord.name,
                    extension: photoFileRecord.extension,
                    file_type: FileType.PATIENT,
                    file_category: FileCategory.IDENTITY_PROOF,
                    associating_id: patientId,
                    mime_type: "",
                    archived_by: null as never,
                    archived_datetime: "",
                    upload_completed: true,
                    created_date: "",
                    uploaded_by: null as never,
                  };
                  void fileManager.viewFile(fileForActions, patientId);
                }
              }}
            >
              {t("view")}
            </button>
            {!!patientId &&
              !!photoFileRecord?.id &&
              !form.formState.dirtyFields.patient_photo_file && (
                <button
                  type="button"
                  className="inline-flex h-8 items-center rounded-md px-2 text-sm font-medium text-primary hover:bg-primary-50"
                  onClick={() => {
                    if (photoFileRecord?.id && patientId) {
                      const fileForActions: FileReadMinimal = {
                        id: photoFileRecord.id,
                        name: photoFileRecord.name,
                        extension: photoFileRecord.extension,
                        file_type: FileType.PATIENT,
                        file_category: FileCategory.IDENTITY_PROOF,
                        associating_id: patientId,
                        mime_type: "",
                        archived_by: null as never,
                        archived_datetime: "",
                        upload_completed: true,
                        created_date: "",
                        uploaded_by: null as never,
                      };
                      void fileManager.downloadFile(fileForActions, patientId);
                    }
                  }}
                >
                  {t("download")}
                </button>
              )}
          </>
        )}
      </div>
      {fileManager.Dialogues}
    </>
  );
};
